import { useLazyQuery } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import {
  SearchGeoItemsByLabelDocument,
  type SearchGeoItemsByLabelQuery,
  type SearchGeoItemsByLabelQueryVariables,
} from "~/features/recordGeoItems/gqls";
import { geoItemToLocation } from "~/features/recordGeoItems/utils/geoItemToLocation";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";
import type { Location } from "~/lib/map/types";

/** Search a geo-item by ja/en label; resolves the first match to a map Location. */
export function useLocationSearch(onFound: (location: Location) => void) {
  const { t } = useTranslation();
  const locale = useDisplayLocale();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);
  const [searchGeoItems] = useLazyQuery<SearchGeoItemsByLabelQuery, SearchGeoItemsByLabelQueryVariables>(
    SearchGeoItemsByLabelDocument,
  );

  const searchLocation = useCallback(
    async (locationText: string) => {
      const label = locationText.trim();
      if (!tenantId || !label) return;
      setProcessing(true);
      try {
        const { data } = await searchGeoItems({
          variables: {
            input: {
              tenantId,
              label,
              limit: 1,
            },
          },
        });
        const geoItem = data?.cnutSearchGeoItems.items[0];
        if (geoItem?.coords) {
          onFound(geoItemToLocation(geoItem, locale));
        } else {
          toast.info(t("feature.recordGeoItems.locationSearch.alert.notFound"));
        }
      } catch (e) {
        // Ignore aborted/superseded requests (AbortError DOMException is `instanceof Error` here).
        if (e instanceof Error && e.name !== "AbortError") toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [searchGeoItems, onFound, t, tenantId, locale],
  );

  return { searchLocation, processing };
}
